import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  clearAllOPFSData,
  clearProjectsDirectory,
  clearAllStorageData
} from '../utils/opfsManager';
import { listProjects, deleteProject, type ProjectListItem } from '../services/storageService';
import { useConfirmDialogStore } from '../stores/confirmDialogStore';
import logger from '../utils/logger';
import * as styles from '../styles/pages/storage-management.css.ts';

/**
 * StorageManagement - ストレージ管理画面
 * OPFSやIndexedDBなどのデータを管理する
 */
export function StorageManagement() {
  const navigate = useNavigate();
  const confirm = useConfirmDialogStore(state => state.confirm);
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [totalSize, setTotalSize] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProjects, setSelectedProjects] = useState<Set<string>>(new Set());

  // ストレージ情報を取得
  useEffect(() => {
    loadStorageInfo();
  }, []);

  const loadStorageInfo = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const projectList = await listProjects();
      setProjects(projectList);
      setTotalSize(projectList.reduce((sum, p) => sum + p.size, 0));
      logger.info('[StorageManagement] Storage info loaded:', { count: projectList.length });
    } catch (err) {
      logger.error('[StorageManagement] Failed to load storage info:', err);
      setError(err instanceof Error ? err.message : 'Failed to load storage info');
    } finally {
      setIsLoading(false);
    }
  };

  // ファイルサイズをフォーマット
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  // プロジェクトを削除
  const handleDeleteProject = async (filename: string, projectName: string) => {
    const confirmed = await confirm({
      title: 'Delete Project',
      message: `Are you sure you want to delete "${projectName}"?`,
      confirmLabel: 'Delete',
      confirmStyle: 'danger',
      testId: 'delete-single-project-confirm'
    });

    if (!confirmed) {
      return;
    }

    try {
      await deleteProject(filename);
      logger.info('[StorageManagement] Project deleted:', filename);
      await loadStorageInfo();
    } catch (err) {
      logger.error('[StorageManagement] Failed to delete project:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete project');
    }
  };

  // 選択したプロジェクトを削除
  const handleDeleteSelected = async () => {
    if (selectedProjects.size === 0) return;

    const confirmed = await confirm({
      title: 'Delete Selected Projects',
      message: `Are you sure you want to delete ${selectedProjects.size} selected project(s)?`,
      confirmLabel: 'Delete',
      confirmStyle: 'danger',
      testId: 'delete-selected-projects-confirm'
    });

    if (!confirmed) {
      return;
    }

    try {
      for (const filename of selectedProjects) {
        await deleteProject(filename);
        logger.info('[StorageManagement] Project deleted:', filename);
      }
      setSelectedProjects(new Set());
      await loadStorageInfo();
    } catch (err) {
      logger.error('[StorageManagement] Failed to delete selected projects:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete projects');
    }
  };

  // すべてのプロジェクトをクリア
  const handleClearAllProjects = async () => {
    const confirmed = await confirm({
      title: 'Delete All Projects',
      message: 'Are you sure you want to delete ALL projects? This cannot be undone.',
      confirmLabel: 'Delete All',
      confirmStyle: 'danger',
      testId: 'delete-all-projects-confirm'
    });

    if (!confirmed) {
      return;
    }

    try {
      await clearProjectsDirectory();
      logger.info('[StorageManagement] All projects cleared');
      await loadStorageInfo();
    } catch (err) {
      logger.error('[StorageManagement] Failed to clear projects:', err);
      setError(err instanceof Error ? err.message : 'Failed to clear projects');
    }
  };

  // 完全リセット
  const handleFullReset = async () => {
    const confirmed1 = await confirm({
      title: 'Full Reset',
      message: 'This will delete ALL data including projects, settings, and cache. Are you sure?',
      confirmLabel: 'Continue',
      confirmStyle: 'danger',
      testId: 'full-reset-confirm-1'
    });

    if (!confirmed1) {
      return;
    }

    const confirmed2 = await confirm({
      title: 'Full Reset - Final Confirmation',
      message: 'This action CANNOT be undone. Are you absolutely sure?',
      confirmLabel: 'Delete Everything',
      confirmStyle: 'danger',
      testId: 'full-reset-confirm-2'
    });

    if (!confirmed2) {
      return;
    }

    try {
      await clearAllStorageData();
      logger.info('[StorageManagement] Full reset completed');
      // ランチャー画面に戻る
      navigate('/');
    } catch (err) {
      logger.error('[StorageManagement] Failed to perform full reset:', err);
      setError(err instanceof Error ? err.message : 'Failed to perform full reset');
    }
  };

  // プロジェクトの選択を切り替え
  const toggleProjectSelection = (filename: string) => {
    const newSelection = new Set(selectedProjects);
    if (newSelection.has(filename)) {
      newSelection.delete(filename);
    } else {
      newSelection.add(filename);
    }
    setSelectedProjects(newSelection);
  };

  // すべて選択/解除
  const toggleSelectAll = () => {
    if (selectedProjects.size === projects.length) {
      setSelectedProjects(new Set());
    } else {
      setSelectedProjects(new Set(projects.map(p => p.filename)));
    }
  };

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <button
          className={styles.backButton}
          onClick={() => navigate('/')}
          title="Back to launcher"
        >
          ← Back
        </button>
        <h1 className={styles.title}>Storage Management</h1>
      </header>

      <main className={styles.main}>
        {error && (
          <div className={styles.error}>
            {error}
          </div>
        )}

        {isLoading ? (
          <div className={styles.loading}>Loading storage information...</div>
        ) : (
          <>
            <div className={styles.summary}>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Total Projects:</span>
                <span className={styles.summaryValue}>{projects.length}</span>
              </div>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Total Size:</span>
                <span className={styles.summaryValue}>{formatFileSize(totalSize)}</span>
              </div>
            </div>

            <div className={styles.actions}>
              <button
                className={styles.actionButton}
                onClick={toggleSelectAll}
                disabled={projects.length === 0}
              >
                {selectedProjects.size === projects.length ? 'Deselect All' : 'Select All'}
              </button>
              <button
                className={`${styles.actionButton} ${styles.deleteButton}`}
                onClick={handleDeleteSelected}
                disabled={selectedProjects.size === 0}
              >
                Delete Selected ({selectedProjects.size})
              </button>
              <button
                className={`${styles.actionButton} ${styles.dangerButton}`}
                onClick={handleClearAllProjects}
                disabled={projects.length === 0}
              >
                Clear All Projects
              </button>
              <button
                className={`${styles.actionButton} ${styles.dangerButton}`}
                onClick={handleFullReset}
              >
                Full Reset
              </button>
            </div>

            <div className={styles.projectList}>
              <h2 className={styles.sectionTitle}>Saved Projects</h2>
              {projects.length === 0 ? (
                <div className={styles.emptyState}>No projects found in storage</div>
              ) : (
                <table className={styles.projectTable}>
                  <thead>
                    <tr>
                      <th className={styles.checkboxColumn}>
                        <input
                          type="checkbox"
                          checked={selectedProjects.size === projects.length}
                          onChange={toggleSelectAll}
                        />
                      </th>
                      <th>Project Name</th>
                      <th>Size</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map((project) => {
                      const projectName = project.metadata?.projectName || project.filename.replace('.mds', '');
                      return (
                        <tr key={project.filename}>
                          <td className={styles.checkboxColumn}>
                            <input
                              type="checkbox"
                              checked={selectedProjects.has(project.filename)}
                              onChange={() => toggleProjectSelection(project.filename)}
                            />
                          </td>
                          <td className={styles.projectName}>
                            {projectName}
                          </td>
                          <td className={styles.projectSize}>
                            {formatFileSize(project.size)}
                          </td>
                          <td className={styles.projectActions}>
                            <button
                              className={styles.deleteIconButton}
                              onClick={() => handleDeleteProject(project.filename, projectName)}
                              title="Delete project"
                            >
                              X
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            <div className={styles.dangerZone}>
              <h2 className={styles.dangerTitle}>⚠️ Danger Zone</h2>
              <p className={styles.dangerDescription}>
                These actions are destructive and cannot be undone. Please be careful.
              </p>
              <div className={styles.dangerActions}>
                <div className={styles.dangerItem}>
                  <div>
                    <h3>Clear All OPFS Data</h3>
                    <p>Remove all files stored in the browser's file system</p>
                  </div>
                  <button
                    className={`${styles.actionButton} ${styles.dangerButton}`}
                    onClick={async () => {
                      const confirmed = await confirm({
                        title: 'Clear OPFS',
                        message: 'Delete all OPFS data? This cannot be undone.',
                        confirmLabel: 'Delete',
                        confirmStyle: 'danger',
                        testId: 'clear-opfs-confirm'
                      });

                      if (confirmed) {
                        await clearAllOPFSData();
                        await loadStorageInfo();
                      }
                    }}
                  >
                    Clear OPFS
                  </button>
                </div>
                <div className={styles.dangerItem}>
                  <div>
                    <h3>Full Application Reset</h3>
                    <p>Delete all data including projects, settings, and cache</p>
                  </div>
                  <button
                    className={`${styles.actionButton} ${styles.dangerButton}`}
                    onClick={handleFullReset}
                  >
                    Full Reset
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export default StorageManagement;